import { Badge } from "@/ui";
import { Calendar } from "lucide-react";
const newsImage = "/assets/website/images/Healthcare_news_and_updates_62ec52b5.png";

interface TimelineItem {
  date: string;
  title: string;
  description: string;
  type: 'company' | 'product' | 'partnership';
}

const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

export default function News() {
  const timelineItems: TimelineItem[] = [
    {
      date: "March 20, 2026",
      title: "AIscendus Plans to Pilot Urgent Care Solution",
      description: "AIscendus announces plans to pilot its agentic AI-powered Urgent Care EMR platform with select urgent care partners.",
      type: "product"
    },
    {
      date: "November 05, 2025",
      title: "AIscendus Inc Announces Healthcare Solutions",
      description: "Comprehensive Healthcare Solutions for Urgent Care, Occupational Medical Services, and Primary Care.",
      type: "product"
    },
    {
      date: "September 15, 2025",
      title: "AIscendus Inc Formation",
      description: "AIscendus Inc was officially formed to revolutionize healthcare through intelligent AI solutions.",
      type: "company"
    }
  ];

  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20">
        {/* Hero Image Section */}
        <div className="relative h-80 mb-20 overflow-hidden">
          <img 
            src={newsImage} 
            alt="Healthcare News and Updates"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Badge variant="secondary" className="mb-4" data-testid="badge-news">
                News & Updates
              </Badge>
              <h1 className="text-3xl md:text-4xl font-normal text-primary mb-4" data-testid="text-news-title" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Latest from AIscendus
              </h1>
              <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-news-description" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Stay updated with our latest developments, partnerships, and insights 
                in healthcare AI innovation.
              </p>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Company Timeline */}
          <div className="mb-12">
            <h2 className="text-3xl font-normal mb-12 text-center" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
              Company Timeline
            </h2>
            
            {/* Vertical Timeline */}
            <div className="relative">
              {/* Vertical Line */}
              <div 
                className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 transform md:-translate-x-1/2"
                style={{ backgroundColor: ACCENT_COLOR }}
              />
              
              {/* Timeline Items */}
              <div className="space-y-12">
                {timelineItems.map((item, index) => (
                  <div 
                    key={index}
                    className={`relative flex items-center ${
                      index % 2 === 0 
                        ? 'md:flex-row' 
                        : 'md:flex-row-reverse'
                    } flex-col md:gap-8`}
                  >
                    {/* Timeline Dot */}
                    <div 
                      className="absolute left-8 md:left-1/2 w-4 h-4 rounded-full transform -translate-x-1/2 border-4 border-white shadow-lg z-10"
                      style={{ backgroundColor: ACCENT_COLOR }}
                    />
                    
                    {/* Content Card */}
                    <div className={`w-full md:w-[calc(50%-2rem)] ml-16 md:ml-0 ${
                      index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'
                    }`}>
                      <div 
                        className="bg-white rounded-xl p-6 shadow-lg border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                        style={{ borderColor: `${PRIMARY_BLUE}20` }}
                      >
                        {/* Date Badge */}
                        <div className={`flex items-center gap-2 mb-3 ${
                          index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'
                        } justify-start`}>
                          <Calendar className="h-4 w-4" style={{ color: ACCENT_COLOR }} />
                          <span className="text-sm font-semibold" style={{ color: PRIMARY_BLUE }}>
                            {item.date}
                          </span>
                        </div>
                        
                        {/* Title */}
                        <h3 
                          className="text-xl font-semibold mb-2" 
                          style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}
                        >
                          {item.title}
                        </h3>
                        
                        {/* Description */}
                        <p className="text-gray-700 leading-relaxed" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                          {item.description}
                        </p>
                        
                        {/* Type Badge */}
                        <div className={`mt-4 ${
                          index % 2 === 0 ? 'md:text-right' : 'md:text-left'
                        } text-left`}>
                          <Badge 
                            className="inline-block"
                            style={{ 
                              backgroundColor: item.type === 'company' ? PRIMARY_BLUE : ACCENT_COLOR,
                              color: 'white'
                            }}
                          >
                            {item.type === 'company' ? 'Company Milestone' : 'Product Launch'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    
                    {/* Spacer for alignment on desktop */}
                    <div className="hidden md:block w-[calc(50%-2rem)]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}